import { useCallback } from 'react'

import { useLocalState } from '@/hooks/useLocalState'

/**
 * Storage key for dismissed product messages
 */
const STORAGE_KEY = 'dismissedProductMessages'

/**
 * Maximum number of dismissed messages to keep in storage
 */
const MAX_DISMISSED_MESSAGES = 100

/**
 * Interface for dismissed messages hook return value
 */
export interface UseDismissedMessagesIProps {
  dismissedMessageIds: string[]
  isMessageDismissed: (messageId: string) => boolean
  dismissMessage: (messageId: string) => void
  clearDismissedMessages: () => void
}

/**
 * Custom hook for managing dismissed product messages using localStorage
 */
export const useDismissedMessages = (): UseDismissedMessagesIProps => {
  const [dismissedMessageIds, setDismissedMessageIds] = useLocalState<string[]>(
    STORAGE_KEY,
    []
  )

  const isMessageDismissed = useCallback(
    (messageId: string): boolean => {
      if (!messageId) return false
      return dismissedMessageIds.includes(messageId)
    },
    [dismissedMessageIds]
  )

  const dismissMessage = useCallback(
    (messageId: string): void => {
      if (!messageId) {
        return
      }

      setDismissedMessageIds((prev) => {
        if (prev.includes(messageId)) {
          return prev
        }
        const newList = [...prev, messageId]

        return newList.slice(-MAX_DISMISSED_MESSAGES)
      })
    },
    [setDismissedMessageIds]
  )

  const clearDismissedMessages = useCallback((): void => {
    setDismissedMessageIds([])
  }, [setDismissedMessageIds])

  return {
    dismissedMessageIds,
    isMessageDismissed,
    dismissMessage,
    clearDismissedMessages,
  }
}
